import api from '@root/src/api/api'

/**
 * Модалка переподключения: уводим клиентов со сторонних провайдеров на наши.
 *
 * Показывать или нет — решает бэк (GET networkFixer/project/dh-migration/prompt).
 * Фронт своего условия не держит намеренно: включение переезда живёт в поле проекта
 * isDhMigration и общем дефолте, и если повторить это здесь, модалка вылезет у
 * проектов, где переезд выключен, а QR не придёт.
 *
 * Сам QR берём из helpers/channels/dhMigration.ts — той же ручки, что и сайдбар.
 *
 * Частоту показа намеренно не ограничиваем: закрыл — увидит снова при следующем
 * входе в проект. Так решено 06.10 после первой волны: за сутки модалку увидели
 * 20 проектов, отсканил один, и ограничение «раз в сессию» только снижало шансы
 * попасть в момент, когда у человека телефон под рукой.
 */

export type ReconnectChannel = {
    connectorId: string
    connectorType: string
    /** provider_type канала у стороннего провайдера */
    providerType: string
    /** наш провайдер, куда переезжаем */
    target: string
}

export async function fetchReconnectChannels(projectId: string): Promise<ReconnectChannel[]> {
    const { data, error } = await api.networkFixer.getDhMigrationPrompt({ projectId })
    if (error || !data?.show || !Array.isArray(data.channels)) {
        return []
    }
    return data.channels as ReconnectChannel[]
}

/**
 * Тексты из макета «Переподключение мессенджеров». Сценарий выбирается по доступу
 * к Мультичату, а не по тарифу: чат бывает докуплен отдельно (согласовано 29.09).
 * Заголовок разрезан надвое — хвост в макете выделен жёлтым и стоит отдельной строкой.
 */
export type ReconnectCopy = {
    titleStart: string
    mobileTitleStart: string
    titleAccent: string
    lead: string
    mobileLead: string
    benefit: string
    complete: string
}

export function getReconnectCopy(chatEnabled: boolean): ReconnectCopy {
    if (chatEnabled) {
        return {
            titleStart: 'Используйте Мультичат на ',
            mobileTitleStart: 'Мультичат на ',
            titleAccent: '100%',
            lead:
                'Переподключите мессенджеры, чтобы диалоги с клиентами появились в Мультичате.',
            mobileLead: 'Переподключите мессенджеры — все диалоги будут в одном окне.',
            benefit: 'Все диалоги с клиентами<br />в одном окне',
            complete: 'Диалоги с клиентами доступны в Мультичате.',
        }
    }
    return {
        titleStart: 'Более стабильная ',
        mobileTitleStart: 'Более стабильная ',
        titleAccent: 'связь',
        lead: 'Переподключите каналы, чтобы снизить риск блокировок',
        mobileLead: 'Переподключите каналы, чтобы снизить риск блокировок',
        benefit: 'Более устойчивое<br />соединение с клиентами',
        complete: 'Мессенджеры подключены через новое соединение.',
    }
}

const CHANNEL_LABELS: Record<string, string> = {
    whatsappChannel: 'WhatsApp',
    telegramChannel: 'Telegram',
    maxChannel: 'MAX',
}

export function getChannelLabel(connectorType: string): string {
    return CHANNEL_LABELS[connectorType] ?? connectorType
}

/** Первый шаг у каждого мессенджера свой — где именно искать привязку устройства. */
const FIRST_STEP: Record<string, string> = {
    whatsappChannel:
        'В WhatsApp на телефоне выберите <strong>«Связанные устройства»</strong>, затем «Привязка устройства».',
    telegramChannel:
        'В настройках Telegram на телефоне выберите <strong>«Устройства»</strong> и подключение нового устройства.',
    maxChannel:
        'В MAX на телефоне откройте <strong>«Настройки»</strong>, затем «Устройства» для сканирования QR-кода.',
}

const LAST_STEP = 'Отсканируйте QR-код. Дождитесь подтверждения переподключения.'

/** На телефоне формулировки короче — места меньше. Тексты из макета. */
const MOBILE_FIRST_STEP: Record<string, string> = {
    whatsappChannel:
        'В WhatsApp: <strong>«Связанные устройства» → «Привязка устройства»</strong>.',
    telegramChannel: 'В Telegram: <strong>«Устройства» → новое устройство</strong>.',
    maxChannel: 'В MAX: <strong>«Настройки» → «Устройства»</strong>.',
}

const MOBILE_LAST_STEP = 'Сканируйте код с экрана компьютера и дождитесь подтверждения.'

export type ReconnectStep = { desktop: string; mobile: string }

export function getChannelSteps(connectorType: string): ReconnectStep[] {
    const first = FIRST_STEP[connectorType]
    const firstMobile = MOBILE_FIRST_STEP[connectorType]
    const last = { desktop: LAST_STEP, mobile: MOBILE_LAST_STEP }
    return first ? [{ desktop: first, mobile: firstMobile ?? first }, last] : [last]
}
